import { useCallback, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import {
  Buildings2,
  Calendar,
  Logout,
  Notification,
  Profile,
  SecuritySafe,
  Building,
} from 'iconsax-reactjs';
import {
  AppShell,
  ContextSelector,
  GlobalSearch,
  NotificationCenter,
  SideNav,
  SideNavBrand,
  ThemeToggle,
  TopBar,
  UserMenu,
  useThemeMenuItems,
  type NavSection,
  type SearchResult,
} from '@/components/layout';
import { useToast } from '@/components/ui';
import { photoPath } from '@/lib/api/photoPath';
import { apiClient } from '@/lib/api/client';
import { useApiImage } from '@/lib/api/useApiImage';
import type { NotificationItem, SearchResult as ApiSearchResult } from '@/lib/api/types';
import { useAuth } from '@/lib/auth/AuthProvider';
import { errorMessage } from '@/lib/format';
import { useApiQuery } from '@/lib/hooks/useApiQuery';
import { TimeZoneProvider } from '@/lib/time';
import { NoOrganizationPage } from '@/features/errors/ErrorPages';
import { notificationsApi } from '@/features/notifications/api';
import { BellFooter, BellSummary } from '@/features/notifications/BellSummary';
import { AcademicYearProvider, useAcademicYear } from './AcademicYearContext';
import { filterNavigation, NAVIGATION, type AppNavItem, type AppNavSection } from './navigation';
import styles from './app.module.css';

const ALL_SCHOOLS = '__all__';

function toNavSections(sections: AppNavSection[]): NavSection[] {
  const mapItem = (item: AppNavItem): NavSection['items'][number] => ({
    id: item.id,
    label: item.label,
    href: item.children ? undefined : item.path,
    icon: item.icon,
    children: item.children?.map(mapItem),
  });
  return sections.map((s) => ({ id: s.id, label: s.label, items: s.items.map(mapItem) }));
}

function ContextSelectors() {
  const { me, switchContext } = useAuth();
  const toast = useToast();
  const years = useAcademicYear();
  const [busy, setBusy] = useState(false);
  if (!me) return null;

  const run = async (organizationId: string, schoolId: string | null) => {
    setBusy(true);
    try {
      await switchContext(organizationId, schoolId);
    } catch (error) {
      toast.error('Could not switch context', { description: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  };

  const org = me.context.organization;
  return (
    <div className={styles.contextBar}>
      {me.organizations.length > 1 && org && (
        <ContextSelector
          label="Organization"
          icon={<Buildings2 size={18} color="currentColor" />}
          value={org.id}
          loading={busy}
          options={me.organizations.map((o) => ({ id: o.id, label: o.name }))}
          onSelect={(id) => void run(id, null)}
        />
      )}
      {org && me.context.schools.length > 0 && (
        <ContextSelector
          label="School"
          icon={<Building size={18} color="currentColor" />}
          value={me.context.school?.id ?? ALL_SCHOOLS}
          loading={busy}
          options={[
            ...(me.context.schools.length > 1 || me.context.permissions.organization.length > 0
              ? [{ id: ALL_SCHOOLS, label: 'All schools', description: org.name }]
              : []),
            ...me.context.schools.map((s) => ({ id: s.id, label: s.name })),
          ]}
          onSelect={(id) => void run(org.id, id === ALL_SCHOOLS ? null : id)}
        />
      )}
      {years.years.length > 0 && (
        <span className={styles.hideNarrow}>
          <ContextSelector
            label="Academic year"
            icon={<Calendar size={18} color="currentColor" />}
            value={years.selected?.id ?? null}
            loading={years.loading}
            options={years.years.map((y) => ({
              id: y.id,
              label: y.name,
              description: [
                y.schoolName ?? 'Organization-wide',
                y.isCurrent ? 'Current' : y.status,
              ].join(' · '),
            }))}
            onSelect={years.select}
          />
        </span>
      )}
    </div>
  );
}

function Notifications() {
  const { me } = useAuth();
  const navigate = useNavigate();
  const orgId = me?.context.organization?.id ?? null;
  const [nonce, setNonce] = useState(0);
  const query = useApiQuery(orgId ? `notifications:${orgId}:${nonce}` : null, (signal) =>
    apiClient.get<{ data: NotificationItem[]; meta: { unreadCount: number } }>('/notifications', {
      signal,
    }),
  );
  // Notification Center: unread counts per category + the category catalog (labels).
  const counts = useApiQuery(orgId ? `notification-counts:${orgId}:${nonce}` : null, (signal) =>
    notificationsApi.unreadCounts(signal).catch(() => null),
  );
  const categories = useApiQuery(orgId ? `notification-categories:${orgId}` : null, (signal) =>
    notificationsApi.categories(signal).catch(() => []),
  );
  const refresh = () => setNonce((n) => n + 1);
  if (!orgId) return null;
  return (
    <NotificationCenter
      loading={query.loading && !query.data}
      unreadCount={query.data?.meta.unreadCount}
      summary={(close) => (
        <BellSummary counts={counts.data} categories={categories.data ?? []} onNavigate={close} />
      )}
      footer={(close) => <BellFooter onNavigate={close} />}
      notifications={(query.data?.data ?? []).map((n) => ({
        id: n.id,
        title: n.title,
        body: n.body ?? undefined,
        createdAt: n.createdAt,
        readAt: n.readAt,
        href: n.linkPath ?? undefined,
      }))}
      onOpenChange={(open) => open && refresh()}
      onMarkRead={(id) => void apiClient.post(`/notifications/${id}/read`).then(refresh)}
      onMarkAllRead={() => void apiClient.post('/notifications/read-all').then(refresh)}
      renderLink={(n, props, children) => (
        <Link to={n.href} className={props.className} onClick={props.onClick}>
          {children}
        </Link>
      )}
      onOpenNotification={(n) => n.href && navigate(n.href)}
    />
  );
}

function Search() {
  const navigate = useNavigate();
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const onQueryChange = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.get<{ data: ApiSearchResult[] }>('/search', { query: { q } });
      setResults(res.data.map((r) => ({ ...r, subtitle: r.subtitle ?? undefined })));
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);
  return (
    <GlobalSearch
      results={results}
      loading={loading}
      onQueryChange={(q) => void onQueryChange(q)}
      onSelect={(r) => navigate(r.href)}
      typeLabels={{ student: 'Students', guardian: 'Guardians', school: 'Schools', user: 'Users' }}
      placeholder="Search students, guardians, schools and users…"
    />
  );
}

function Shell() {
  const { me, satisfies, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const themeItems = useThemeMenuItems();

  const sections = useMemo(
    () => toNavSections(filterNavigation(NAVIGATION, satisfies)),
    [satisfies],
  );
  const avatarSrc = useApiImage(photoPath('/me/avatar', me?.user));
  if (!me) return null;

  const tz = me.context.timeZone;
  const roleSummary = [me.context.school?.name ?? me.context.organization?.name]
    .filter(Boolean)
    .join(' · ');

  return (
    <TimeZoneProvider timeZone={tz.timeZone} source={tz.source} locale={me.context.locale}>
      <AppShell
        footer={<>Copyright © 2026 Code Bridges LLC - IntellSuite</>}
        sideNav={
          <SideNav
            sections={sections}
            activeHref={location.pathname}
            header={
              <SideNavBrand
                href="/dashboard"
                renderLink={(href, props, children) => (
                  <Link to={href} className={props.className}>
                    {children}
                  </Link>
                )}
              />
            }
            renderLink={(item, props, children) => (
              <Link
                to={item.href}
                className={props.className}
                aria-current={props['aria-current']}
                onClick={props.onClick}
              >
                {children}
              </Link>
            )}
          />
        }
        topBar={
          <TopBar
            left={<ContextSelectors />}
            center={me.context.organization ? <Search /> : undefined}
            right={
              <>
                <Notifications />
                <ThemeToggle />
                <UserMenu
                  name={me.user.displayName}
                  avatarSrc={avatarSrc}
                  subtitle={roleSummary || me.user.email}
                  items={[
                    {
                      id: 'profile',
                      label: 'Profile',
                      icon: <Profile size={18} color="currentColor" />,
                      onSelect: () => navigate('/account/profile'),
                    },
                    {
                      id: 'security',
                      label: 'Security & sessions',
                      icon: <SecuritySafe size={18} color="currentColor" />,
                      onSelect: () => navigate('/account/security'),
                    },
                    {
                      id: 'notification-preferences',
                      label: 'Notification preferences',
                      icon: <Notification size={18} color="currentColor" />,
                      onSelect: () => navigate('/account/notifications'),
                    },
                    { type: 'separator', id: 'sep-1' },
                    ...themeItems,
                    { type: 'separator', id: 'sep-2' },
                    {
                      id: 'logout',
                      label: 'Sign out',
                      tone: 'danger',
                      icon: <Logout size={18} color="currentColor" />,
                      onSelect: () => void logout().then(() => navigate('/login')),
                    },
                  ]}
                />
              </>
            }
          />
        }
      >
        {me.context.organization ||
        location.pathname.startsWith('/account') ||
        location.pathname.startsWith('/platform') ? (
          <Outlet />
        ) : (
          <NoOrganizationPage />
        )}
      </AppShell>
    </TimeZoneProvider>
  );
}

export function AppLayout() {
  return (
    <AcademicYearProvider>
      <Shell />
    </AcademicYearProvider>
  );
}
